import type { RawMqttMessage } from '@/lib/mqtt/types';
import { useEffect, useMemo, useState } from 'react';

/** Adaptive rate readout: a formatted number plus the unit it's expressed in.
 *  Slow topics roll up to /min or /hr so they never read a useless "0 msg/s". */
export interface RateDisplay {
  value: string;
  unit: 'msg/s' | 'msg/min' | 'msg/hr';
}

/** Trailing window the live count is measured over. A burst of N messages
 *  delivered in a few ms (retained-message flush, reconnect backlog, TCP
 *  batching) can contribute at most N/windowSec — so it can never extrapolate a
 *  sub-second spike into a fictional "9000 msg/s". */
const RATE_WINDOW_MS = 2000;

/** At least this many messages must land in the window to trust the windowed
 *  count; below it the topic is too sparse and we fall back to inter-arrival. */
const MIN_WINDOW_SAMPLES = 2;

/** Most recent messages to average the cadence over in the slow-topic fallback.
 *  Long enough to smooth jitter, short enough to react when the cadence
 *  changes. */
const SAMPLE_WINDOW = 30;

const fmt = (x: number): string => (x >= 10 ? String(Math.round(x)) : x.toFixed(1));

/** Express a per-second rate in the largest unit that yields a readable
 *  (≥1) number: msg/s → msg/min → msg/hr. */
function formatRate(perSec: number): RateDisplay {
  if (perSec >= 1) return { value: fmt(perSec), unit: 'msg/s' };
  const perMin = perSec * 60;
  if (perMin >= 1) return { value: fmt(perMin), unit: 'msg/min' };
  return { value: fmt(perSec * 3600), unit: 'msg/hr' };
}

/** Pure rate computation over a topic's buffered messages (newest-first).
 *  Primary path is a trailing fixed-time window count — burst-proof, since a
 *  flush of many messages in a few ms can contribute at most window-many to a
 *  per-second figure. Sparse topics (too few messages in the window) fall back
 *  to the average inter-arrival interval, so a 1-msg-per-30s topic still reads
 *  "2.0 msg/min" rather than 0. A topic silent past a few expected intervals
 *  reads 0 so a dead feed doesn't show a stale rate. */
export function computeMessageRate(
  messages: Pick<RawMqttMessage, 'receivedAt'>[],
  now: number,
): RateDisplay {
  if (messages.length < 2) return { value: '0', unit: 'msg/s' };

  // Burst-proof primary: count whatever actually arrived in the trailing window.
  // Messages are newest-first, so we can stop at the first one out of window.
  const windowStart = now - RATE_WINDOW_MS;
  let inWindow = 0;
  for (const m of messages) {
    if (m.receivedAt < windowStart) break;
    inWindow++;
  }
  if (inWindow >= MIN_WINDOW_SAMPLES) {
    return formatRate(inWindow / (RATE_WINDOW_MS / 1000));
  }

  // Sparse fallback: average inter-arrival interval over recent samples so slow
  // topics roll up to msg/min · msg/hr instead of reading a useless 0.
  const recent = messages.slice(0, SAMPLE_WINDOW);
  const newest = recent[0]!.receivedAt;
  const oldest = recent[recent.length - 1]!.receivedAt;
  const spanSec = (newest - oldest) / 1000;
  if (spanSec <= 0) return { value: '0', unit: 'msg/s' };
  const avgIntervalSec = spanSec / (recent.length - 1);
  // Idle clamp: silent for > 3 expected intervals (min 10s) ⇒ treat as stalled.
  const idleSec = (now - newest) / 1000;
  if (idleSec > Math.max(10, avgIntervalSec * 3)) return { value: '0', unit: 'msg/s' };
  return formatRate(1 / avgIntervalSec);
}

/** Rolling msg/s for the selected topic, derived purely on the client from the
 *  arrival timestamps of its buffered messages. Re-evaluates every second (via
 *  a tick) so slow and newly-idle topics update without waiting for the next
 *  message. */
export function useMessageRate(messages: RawMqttMessage[]): RateDisplay {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, []);

  // `tick` is intentionally a dep: it forces a recompute against a fresh `now`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: tick is a time trigger; Date.now() is read at compute time.
  return useMemo(() => computeMessageRate(messages, Date.now()), [messages, tick]);
}
